<style lang="scss" scoped>
.search-select {
  position: relative;
}

.search-select-input {
  appearance: none;
  text-align: left;
  cursor: pointer;
  user-select: none;
  display: block;
  width: 100%;
  height: 34px;
  padding: 6px 12px;
  line-height: 1.42857143;
  color: #555;
  background-color: #fff;
  background-image: none;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  box-shadow: none;
  border-color: #d2d6de;
  white-space: nowrap;
  display: flex;
  align-items: center;

  &:focus {
    border-color: #3c8dbc;
  }
}

.search-select-placeholder,
.search-select-text {
  flex-grow: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.search-select__arrow {
  transition: all 200ms ease-in-out;

  &::before {
    display: block;
    color: #999;
    border-style: solid;
    border-width: 5px 5px 0;
    border-color: #999 transparent transparent;
    content: '';
  }

  &__open {
    transform: rotate(180deg);
  }
}

.search-select-dropdown {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  position: absolute;
  right: 0;
  left: 0;
  background-color: #fff;
  border-radius: 0.25rem;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  z-index: 50;
  border: solid 1px #d2d6de;
  display: flex;
  flex-direction: column;
}

.search-select-search {
  display: block;
  padding: 0.5rem 0.75rem;
  background-color: #ededed;
  border: 1px solid #ccc;
  margin: 0.5rem;
}

.search-select-options {
  list-style: none;
  padding: 0;
  position: relative;
  overflow-y: auto;
  max-height: 206px;
  margin: 0;
}

.search-select-option {
  padding: 4px 6px;
  cursor: pointer;
  user-select: none;
  border-bottom: solid 1px #d2d6de;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #e9e9e9;
    color: rgb(94, 94, 94);
  }

  &.is-active {
    background-color: #269add;
    color: #fff;
  }
}

.search-select-empty {
  text-align: center;
  padding: 0.5rem;
}
</style>

<template>
  <on-click-outside :do="close">
    <div class="search-select" :class="{ 'is-active': isOpen }">
      <ts-loading-banner :loading="fetching">
        <button
          ref="button"
          @click="open"
          type="button"
          class="search-select-input"
        >
          <span
            v-if="option !== null"
            class="search-select-text"
            :title="`${option.staff_id} | ${option.name_en}`"
            >{{ option.staff_id }} | {{ option.name_en }}</span
          >
          <span v-else class="search-select-placeholder">--Select--</span>
          <span
            class="search-select__arrow"
            :class="{ 'search-select__arrow__open': isOpen }"
          ></span>
        </button>
        <div ref="dropdown" v-show="isOpen" class="search-select-dropdown">
          <input
            class="search-select-search"
            v-model="localSearch"
            ref="search"
            @keydown.esc="close"
            @keydown.up="highlightPrev"
            @keydown.down="highlightNext"
            @keydown.enter.prevent="selectHighlighted"
          />
          <ul
            ref="options"
            v-show="filteredOptions.length > 0"
            class="search-select-options"
          >
            <li
              class="search-select-option"
              v-for="(option, i) in filteredOptions"
              :key="option.staff_id"
              @click="toggleSelect(option)"
              :class="{ 'is-active': i === highlightedIndex }"
            >
              <table style="width: 100%">
                <tr>
                  <td class="ts-whitespace-no-wrap" width="20%">
                    {{ option.staff_id }}
                  </td>
                  <td class="ts-whitespace-no-wrap" width="60%">
                    {{ option.name_en }}
                  </td>
                  <td class="ts-whitespace-no-wrap" width="20%">
                    <span v-if="option.gender == 1">{{
                      $t('employee.male')
                    }}</span>
                    <span v-else>{{ $t('employee.female') }}</span>
                  </td>
                </tr>
              </table>
            </li>
          </ul>
          <div
            v-show="filteredOptions.length === 0"
            class="search-select-empty"
          >
            No employee found for
            <b>"{{ localSearch }}"</b>
          </div>
        </div>
      </ts-loading-banner>
    </div>
  </on-click-outside>
</template>

<script>
import Popper from 'popper.js'
import { isEqual } from 'lodash'
import OnClickOutside from '@/components/OnClickOutside.vue'

export default {
  name: 'SelectEmployee',
  components: {
    OnClickOutside
  },
  props: ['value', 'att_date'],
  data() {
    return {
      options: [],
      filteredOptions: [],
      localSearch: '',
      isOpen: false,
      highlightedIndex: null,
      option: null,
      fetching: false
    }
  },
  created() {
    this.fetchData()
  },
  beforeDestroy() {
    this.popper !== undefined && this.popper.destroy()
  },
  methods: {
    async fetchData() {
      this.fetching = true
      await this.$store
        .dispatch('employeeDailyTimescan/getEmployee')
        .then(response => {
          this.options = Array.isArray(response.data) ? response.data : []
          this.filteredOptions = this.options
          this.setDefaultOption()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.fetching = false
        })
    },
    filterOptions() {
      const search = this.localSearch.toLowerCase()
      if (!search) {
        this.filteredOptions = this.options
      } else {
        this.filteredOptions = this.options.filter(
          opt =>
            (opt.name_en && opt.name_en.toLowerCase().includes(search)) ||
            (opt.name_kh && opt.name_kh.toLowerCase().includes(search)) ||
            String(opt.staff_id).includes(search)
        )
      }
      this.highlightedIndex = 0
    },
    open() {
      if (!this.isOpen) {
        this.isOpen = true
        this.$nextTick(() => {
          this.setupPopper()
          this.$refs.search.focus()
          this.scrollToHighlighted()
        })
      } else {
        this.isOpen = false
      }
    },
    setupPopper() {
      if (this.popper === undefined) {
        this.popper = new Popper(this.$refs.button, this.$refs.dropdown, {
          placement: 'bottom',
          positionFixed: true,
          modifiers: {
            offset: {
              enabled: true,
              fn: data => {
                data.offsets.popper.left = data.offsets.reference.left
                data.offsets.popper.right = data.offsets.reference.right
                data.offsets.popper.width = data.styles.width = Math.round(
                  data.offsets.reference.width
                )
                return data
              },
              order: 840
            }
          }
        })
      } else {
        this.popper.scheduleUpdate()
      }
    },
    close() {
      if (!this.isOpen) {
        return
      }
      this.isOpen = false
    },
    toggleSelect(option) {
      if (this.option && this.option.staff_id === option.staff_id) {
        this.option = null
        this.$emit('input', null)
        this.$emit('change', null)
        this.highlightedIndex = null
      } else {
        this.option = option
        this.$emit('input', option.staff_id)
        this.$emit('change', option.staff_id)
        this.highlightedIndex = this.filteredOptions.indexOf(option)
      }
      this.close()
    },
    selectHighlighted() {
      if (this.filteredOptions[this.highlightedIndex]) {
        this.toggleSelect(this.filteredOptions[this.highlightedIndex])
      }
    },
    scrollToHighlighted() {
      const index = this.highlightedIndex === null ? 0 : this.highlightedIndex
      if (
        this.$refs.options &&
        this.$refs.options.children.length > 0 &&
        this.$refs.options.children[index]
      ) {
        this.$refs.options.children[index].scrollIntoView({ block: 'nearest' })
      }
    },
    highlight(index) {
      this.highlightedIndex = index
      if (this.highlightedIndex < 0) {
        this.highlightedIndex = this.filteredOptions.length - 1
      }
      if (this.highlightedIndex > this.filteredOptions.length - 1) {
        this.highlightedIndex = 0
      }
      this.scrollToHighlighted()
    },
    highlightPrev() {
      this.highlight(this.highlightedIndex - 1)
    },
    highlightNext() {
      this.highlight(this.highlightedIndex + 1)
    },
    setDefaultOption() {
      const option = this.options.find(opt => opt.staff_id === this.value)
      if (option === undefined) {
        this.option = null
        return
      }
      this.option = option
      this.highlightedIndex = this.options.indexOf(option)
    }
  },
  watch: {
    // Local search — filter already-loaded options, no API call
    localSearch(val) {
      this.filterOptions()
    },
    // Reset selection when value prop changes externally
    value: {
      handler(val) {
        if (!val) {
          this.option = null
          return
        }
        if (this.option && isEqual(val, this.option.staff_id)) return
        this.setDefaultOption()
      },
      immediate: true
    },
    // Refetch when date changes
    att_date(val) {
      if (val) {
        this.fetchData()
      }
    }
  }
}
</script>
